import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { NotificationBanner } from '../components/NotificationManager';
import { LocationPermissionBanner } from '../components/LocationManager';
import { ThemeToggle } from '../context/ThemeContext';
import { playClick, playSuccess } from '../utils/SoundManager';
import { requestNotificationPermission, isNotificationGranted, hasActiveSubscription, isNotificationSupported } from '../utils/NotificationManager';
import { MotivationalToast } from '../components/MagicEffects';
import NotificationCenter, { NotificationPermissionBanner } from '../components/NotificationCenter';
import HeaderNotifications from '../components/HeaderNotifications';
import SmartHighlightCard from '../components/SmartHighlightCard';
import { getRandomQuote } from '../utils/MarriageQuotes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeQuote, setWelcomeQuote] = useState(() => getRandomQuote());
  const [stats, setStats] = useState({
    unreadMessages: 0,
    pendingRequests: 0,
    profileCompletion: 0
  });
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [showMotivation, setShowMotivation] = useState(false);

  useEffect(() => {
    fetchUserData();
    // Check if first login
    const welcomed = localStorage.getItem('welcomed');
    if (!welcomed) {
      setShowWelcome(true);
      localStorage.setItem('welcomed', 'true');
    }
    
    // طلب إذن الإشعارات بعد 3 ثواني (باستخدام المكون الجديد)
    const checkNotifications = async () => {
      const notificationAsked = localStorage.getItem('notificationAsked');
      const hasSubscription = await hasActiveSubscription();
      
      if (!notificationAsked && isNotificationSupported() && (!isNotificationGranted() || !hasSubscription)) {
        setTimeout(() => {
          setShowNotificationPrompt(true);
        }, 3000);
      }
    };
    checkNotifications();

    // إظهار رسالة تحفيزية عشوائية كل 60 ثانية
    const motivationInterval = setInterval(() => {
      if (Math.random() > 0.7) { // 30% احتمالية
        setShowMotivation(true);
      }
    }, 60000);

    return () => clearInterval(motivationInterval);
  }, []);

  const getHeaders = () => {
    const userId = localStorage.getItem('userId');
    return userId ? { 'X-User-Id': userId } : {};
  };

  const fetchUserData = async () => {
    try {
      const [userRes, subRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true, headers: getHeaders() }),
        axios.get(`${BACKEND_URL}/api/subscription/status`, { withCredentials: true, headers: getHeaders() }).catch(() => null)
      ]);
      
      setUser(userRes.data);
      localStorage.setItem('userId', userRes.data.user_id);
      localStorage.setItem('user', JSON.stringify(userRes.data));
      if (subRes) setSubscriptionStatus(subRes.data);
      
      // Calculate profile completion
      const requiredFields = ['gender', 'age', 'city', 'job', 'marital_status', 'prayer_level'];
      const filledFields = requiredFields.filter(f => userRes.data[f]);
      setStats(prev => ({
        ...prev,
        profileCompletion: Math.round((filledFields.length / requiredFields.length) * 100)
      }));

      // Fetch pending requests
      try {
        const reqRes = await axios.get(`${BACKEND_URL}/api/connections/received`, { withCredentials: true, headers: getHeaders() });
        setStats(prev => ({
          ...prev,
          pendingRequests: reqRes.data.requests?.length || 0
        }));
      } catch (e) {}

    } catch (err) {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true, headers: getHeaders() });
      localStorage.removeItem('userId');
      localStorage.removeItem('user');
      localStorage.removeItem('welcomed');
      navigate('/');
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>جاري التحميل...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--light-bg)' }}>
      {/* Welcome Modal for First Login */}
      {showWelcome && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div className="zoom-in" style={{
            background: 'linear-gradient(135deg, #ffffff, #f8f9fa)',
            borderRadius: '28px',
            padding: '40px 32px',
            maxWidth: '420px',
            textAlign: 'center',
            boxShadow: '0 25px 80px rgba(0,0,0,0.3)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* خلفية زخرفية */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              left: '-50px',
              width: '150px',
              height: '150px',
              background: 'linear-gradient(135deg, rgba(255, 107, 157, 0.2), rgba(192, 110, 237, 0.2))',
              borderRadius: '50%',
              filter: 'blur(40px)'
            }} />
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              right: '-30px',
              width: '100px',
              height: '100px',
              background: 'linear-gradient(135deg, rgba(79, 172, 254, 0.2), rgba(0, 242, 254, 0.2))',
              borderRadius: '50%',
              filter: 'blur(30px)'
            }} />

            {/* أيقونة متحركة */}
            <div className="heartbeat" style={{ fontSize: '70px', marginBottom: '20px', position: 'relative', zIndex: 1 }}>🎉</div>
            
            {/* العنوان */}
            <h2 style={{ 
              background: 'linear-gradient(135deg, #FF6B9D, #C06EED)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              marginBottom: '16px', 
              fontSize: '28px',
              fontWeight: '800',
              position: 'relative',
              zIndex: 1
            }}>
              أهلاً وسهلاً {user?.name}! 💕
            </h2>
            
            {/* الرسالة - حكمة عشوائية */}
            <p style={{ 
              color: 'var(--text-light)', 
              marginBottom: '12px', 
              lineHeight: '1.9',
              fontSize: '16px',
              position: 'relative',
              zIndex: 1
            }}>
              نسعد بانضمامك إلى <strong style={{color: 'var(--primary)'}}>وِداد</strong>
            </p>

            {/* اقتباس / حكمة */}
            <div style={{
              background: 'rgba(255, 107, 157, 0.08)',
              border: '1px solid rgba(255, 107, 157, 0.18)',
              borderRadius: '14px',
              padding: '18px 16px',
              marginBottom: '24px',
              position: 'relative',
              zIndex: 1
            }}>
              <div style={{ fontSize: '20px', marginBottom: '8px', opacity: 0.5 }}>❝</div>
              <p style={{
                fontSize: '15px',
                color: 'var(--text-dark)',
                lineHeight: 1.9,
                fontWeight: '600',
                margin: 0,
                fontStyle: 'italic'
              }}>
                {welcomeQuote.text}
              </p>
              <p style={{
                fontSize: '12px',
                color: 'var(--primary)',
                marginTop: '10px',
                fontWeight: '700',
                marginBottom: 0
              }}>
                — {welcomeQuote.source}
              </p>
            </div>
            
            {/* الزر */}
            <button 
              className="btn btn-glow ripple"
              onClick={() => { playSuccess(); setShowWelcome(false); }}
              style={{ 
                width: '100%',
                background: 'linear-gradient(135deg, #FF6B9D, #C06EED)',
                color: 'white',
                padding: '16px',
                borderRadius: '16px',
                border: 'none',
                fontSize: '18px',
                fontWeight: '700',
                boxShadow: '0 10px 30px rgba(255, 107, 157, 0.4)',
                position: 'relative',
                zIndex: 1
              }}
            >
              ابدأ رحلتك الآن 🚀
            </button>

            {/* نجوم زخرفية */}
            <div style={{ 
              position: 'absolute', 
              top: '20px', 
              right: '20px',
              fontSize: '20px',
              animation: 'sparkleFloat 2s infinite'
            }}>✨</div>
            <div style={{ 
              position: 'absolute', 
              bottom: '60px', 
              left: '20px',
              fontSize: '16px',
              animation: 'sparkleFloat 2s infinite 0.5s'
            }}>💫</div>
          </div>
        </div>
      )}

      {/* Notification Permission Prompt - المكون المحسن */}
      {showNotificationPrompt && (
        <NotificationPermissionBanner onClose={() => setShowNotificationPrompt(false)} />
      )}

      {/* Header - صفين */}
      <div className="header" style={{ padding: '10px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 12px' }}>
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            {/* اللوجو - يمين (أول عنصر في RTL) */}
            <h1 className="logo" style={{ fontSize: '22px', margin: 0 }}>💍<br/>وِداد</h1>
            
            {/* الأيقونات - الوسط */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '8px',
              justifyItems: 'center'
            }}>
              <button className="nav-link" style={{background: 'var(--light-bg)', color: 'var(--primary)', padding: '12px 16px', borderRadius: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px'}}>
                <span style={{fontSize: '24px'}}>🏠</span>
                <span style={{fontSize: '12px', fontWeight: '600'}}>الرئيسية</span>
              </button>
              <button className="nav-link" onClick={() => navigate('/search')} style={{padding: '12px 16px', borderRadius: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px'}}>
                <span style={{fontSize: '24px'}}>🔍</span>
                <span style={{fontSize: '12px', fontWeight: '600'}}>البحث</span>
              </button>
              <button className="nav-link" onClick={() => navigate('/messages')} style={{padding: '12px 16px', borderRadius: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', position: 'relative'}}>
                <span style={{fontSize: '24px'}}>💬</span>
                <span style={{fontSize: '12px', fontWeight: '600'}}>الرسائل</span>
                {stats.pendingRequests > 0 && (
                  <span style={{ 
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    background: 'var(--error)', 
                    color: 'white', 
                    borderRadius: '50%', 
                    padding: '3px 6px', 
                    fontSize: '10px',
                    fontWeight: 'bold'
                  }}>
                    {stats.pendingRequests}
                  </span>
                )}
              </button>
              <button className="nav-link" onClick={() => navigate('/pricing')} style={{padding: '12px 16px', borderRadius: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px'}}>
                <span style={{fontSize: '24px'}}>💎</span>
                <span style={{fontSize: '12px', fontWeight: '600'}}>الباقات</span>
              </button>
              <button className="nav-link" onClick={() => navigate('/profile-setup')} style={{padding: '12px 16px', borderRadius: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px'}}>
                <span style={{fontSize: '24px'}}>📝</span>
                <span style={{fontSize: '12px', fontWeight: '600'}}>مواصفاتي</span>
              </button>
              <button className="nav-link" onClick={() => navigate('/account')} style={{padding: '12px 16px', borderRadius: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px'}}>
                <span style={{fontSize: '24px'}}>👤</span>
                <span style={{fontSize: '12px', fontWeight: '600'}}>حسابي</span>
              </button>
            </div>
            
            {/* الإشعارات وزر الخروج - يسار (آخر عنصر في RTL) */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <HeaderNotifications />
                <NotificationCenter />
              </div>
              <button 
                onClick={handleLogout}
                style={{ 
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%)', 
                  border: 'none', 
                  color: 'white', 
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600',
                  padding: '10px 16px',
                  borderRadius: '10px',
                  boxShadow: '0 2px 8px rgba(255,107,107,0.3)'
                }}
              >
                خروج
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container" style={{ paddingTop: '30px' }}>
        {/* Smart Highlight Card - يعرض أهم إجراء مطلوب */}
        <SmartHighlightCard 
          user={user} 
          pendingRequests={stats.pendingRequests} 
          profileCompletion={stats.profileCompletion} 
        />

        {/* Welcome Card */}
        <div className="card" style={{ 
          background: 'linear-gradient(135deg, var(--primary) 0%, var(--purple) 100%)',
          color: 'white',
          marginBottom: '14px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ flex: 1, minWidth: '250px' }}>
              <h2 style={{ fontSize: '26px', marginBottom: '8px' }}>
                مرحباً {user?.name || 'بك'}! 👋
              </h2>
              {subscriptionStatus?.is_premium && (
                <p style={{ opacity: 0.9, fontSize: '15px', marginBottom: '12px' }}>
                  ⭐ أنت عضو مميز - استمتع بجميع المميزات!
                </p>
              )}
              
              {/* اقتباس عشوائي عن الزواج */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(8px)',
                borderRadius: '12px',
                padding: '14px 16px',
                marginTop: '12px',
                borderRight: '3px solid rgba(255, 255, 255, 0.5)'
              }}>
                <p style={{
                  fontSize: '14px',
                  color: 'white',
                  lineHeight: 1.8,
                  margin: 0,
                  fontWeight: '500',
                  fontStyle: 'italic'
                }}>
                  ❝ {welcomeQuote.text} ❞
                </p>
                <p style={{
                  fontSize: '11px',
                  color: 'rgba(255,255,255,0.85)',
                  marginTop: '6px',
                  fontWeight: '700',
                  marginBottom: 0
                }}>
                  — {welcomeQuote.source}
                </p>
              </div>
            </div>
            {subscriptionStatus?.is_premium && (
              <div style={{ 
                background: 'rgba(255,255,255,0.2)', 
                padding: '10px 20px', 
                borderRadius: '20px',
                fontSize: '14px',
                alignSelf: 'flex-start'
              }}>
                ⭐ عضو مميز
              </div>
            )}
          </div>
        </div>

        {/* Location Permission Banner */}
        <LocationPermissionBanner />

        {/* Privacy Pause Active Banner */}
        {user?.is_paused && (
          <div
            data-testid="dashboard-pause-banner"
            className="card no-hover"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.18), rgba(255, 152, 0, 0.12))',
              border: '2px solid rgba(255, 152, 0, 0.4)',
              marginBottom: '14px',
              cursor: 'pointer'
            }}
            onClick={() => navigate('/account')}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '32px' }}>🌙</span>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <h4 style={{ color: '#92400E', margin: '0 0 4px' }}>حسابك مخفي مؤقتاً</h4>
                <p style={{ color: '#92400E', fontSize: '13px', margin: 0 }}>
                  {user?.paused_until
                    ? <>يعود تلقائياً في: <strong>{new Date(user.paused_until).toLocaleDateString('ar-SA', { year: 'numeric', month: 'long', day: 'numeric' })}</strong></>
                    : 'مدة الإخفاء غير محددة — استأنف من «حسابي»'}
                </p>
              </div>
              <button
                className="btn btn-primary"
                style={{ padding: '10px 18px', fontSize: '13px' }}
                onClick={(e) => { e.stopPropagation(); navigate('/account'); }}
              >
                إدارة الإخفاء
              </button>
            </div>
          </div>
        )}

        {/* Profile Completion Warning */}
        {stats.profileCompletion < 100 && (
          <div className="card" style={{ 
            background: 'rgba(255, 193, 7, 0.15)', 
            border: '2px solid rgba(255, 193, 7, 0.4)',
            marginBottom: '14px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h4 style={{ color: 'var(--text-dark)', marginBottom: '4px' }}>⚠️ أكمل ملفك الشخصي</h4>
                <p style={{ color: 'var(--text-light)', fontSize: '14px' }}>
                  ملفك مكتمل بنسبة {stats.profileCompletion}% فقط. أكمله لزيادة فرص ظهورك!
                </p>
              </div>
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/profile-setup')}
              >
                إكمال الملف
              </button>
            </div>
          </div>
        )}

        {/* Quick Actions Grid */}
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
          
          {/* AI Suggestions - Featured */}
          <div 
            className="card profile-card card-float stagger-item" 
            onClick={() => { playClick(); navigate('/ai-suggestions'); }}
            style={{ 
              textAlign: 'center', 
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #9C27B0, #7B1FA2)',
              color: 'white',
              gridColumn: 'span 2',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <div className="floating" style={{ fontSize: '50px', marginBottom: '12px' }}>🤖✨</div>
            <h4 style={{ marginBottom: '6px', color: 'white', fontSize: '18px' }}>اقتراحات ذكية</h4>
            <p style={{ fontSize: '14px', opacity: 0.9 }}>الذكاء الاصطناعي يقترح لك الأنسب!</p>
            <div style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              background: 'rgba(255,255,255,0.2)',
              padding: '4px 12px',
              borderRadius: '20px',
              fontSize: '11px',
              fontWeight: '600'
            }}>
              ✨ جديد
            </div>
          </div>

          <div 
            className="card profile-card card-float stagger-item" 
            onClick={() => { playClick(); navigate('/search'); }}
            style={{ textAlign: 'center', cursor: 'pointer' }}
          >
            <div className="icon-3d" style={{ fontSize: '50px', marginBottom: '12px' }}>🔍</div>
            <h4 style={{ marginBottom: '6px' }}>البحث</h4>
            <p style={{ fontSize: '13px', opacity: 0.8 }}>ابحث عن شريك حياتك</p>
          </div>

          <div 
            className="card profile-card card-float stagger-item" 
            onClick={() => { playClick(); navigate('/messages'); }}
            style={{ textAlign: 'center', cursor: 'pointer', position: 'relative' }}
          >
            {stats.pendingRequests > 0 && (
              <div className="badge-bounce" style={{
                position: 'absolute',
                top: '12px',
                left: '12px',
                background: 'linear-gradient(135deg, #FF5757, #FF3333)',
                color: 'white',
                borderRadius: '50%',
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: '700',
                boxShadow: '0 4px 12px rgba(255, 87, 87, 0.4)'
              }}>
                {stats.pendingRequests}
              </div>
            )}
            <div className="icon-3d" style={{ fontSize: '50px', marginBottom: '12px' }}>💬</div>
            <h4 style={{ marginBottom: '6px' }}>الرسائل</h4>
            <p style={{ fontSize: '13px', opacity: 0.8 }}>المحادثات والطلبات</p>
          </div>

          <div 
            className="card profile-card card-float stagger-item" 
            onClick={() => { playClick(); navigate('/photos'); }}
            style={{ textAlign: 'center', cursor: 'pointer' }}
          >
            <div className="icon-3d" style={{ fontSize: '50px', marginBottom: '12px' }}>📷</div>
            <h4 style={{ marginBottom: '6px' }}>صوري</h4>
            <p style={{ fontSize: '13px', opacity: 0.8 }}>إدارة الصور الشخصية</p>
          </div>

          <div 
            className="card profile-card card-float stagger-item" 
            onClick={() => { playClick(); navigate('/profile-setup'); }}
            style={{ textAlign: 'center', cursor: 'pointer' }}
          >
            <div className="icon-3d" style={{ fontSize: '50px', marginBottom: '12px' }}>✏️</div>
            <h4 style={{ marginBottom: '6px' }}>ملفي</h4>
            <p style={{ fontSize: '13px', opacity: 0.8 }}>تعديل المعلومات</p>
          </div>

          <div 
            className="card profile-card card-float stagger-item" 
            onClick={() => { playClick(); navigate('/stories'); }}
            style={{ textAlign: 'center', cursor: 'pointer' }}
          >
            <div className="icon-3d" style={{ fontSize: '50px', marginBottom: '12px' }}>📚</div>
            <h4 style={{ marginBottom: '6px' }}>قصص ونصائح</h4>
            <p style={{ fontSize: '13px', opacity: 0.8 }}>قصص نجاح ونصائح</p>
          </div>

          <div 
            data-testid="stats-card"
            className="card profile-card card-float stagger-item" 
            onClick={() => { playClick(); navigate('/stats'); }}
            style={{ textAlign: 'center', cursor: 'pointer' }}
          >
            <div className="icon-3d" style={{ fontSize: '50px', marginBottom: '12px' }}>📊</div>
            <h4 style={{ marginBottom: '6px' }}>إحصائياتي</h4>
            <p style={{ fontSize: '13px', opacity: 0.8 }}>الزيارات والإعجابات</p>
          </div>

          <div 
            data-testid="referral-card"
            className="card profile-card card-float stagger-item" 
            onClick={() => { playClick(); navigate('/referral'); }}
            style={{ 
              textAlign: 'center', 
              cursor: 'pointer',
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.12), rgba(255, 165, 0, 0.08))',
              border: '1px solid rgba(255, 165, 0, 0.25)'
            }}
          >
            <div className="icon-3d" style={{ fontSize: '50px', marginBottom: '12px' }}>🎁</div>
            <h4 style={{ marginBottom: '6px' }}>ادعُ صديقاً</h4>
            <p style={{ fontSize: '13px', opacity: 0.8 }}>اكسب 7 أيام Premium 🎉</p>
          </div>

          <div 
            data-testid="favorites-card"
            className="card profile-card card-float stagger-item" 
            onClick={() => { playClick(); navigate('/favorites'); }}
            style={{ textAlign: 'center', cursor: 'pointer' }}
          >
            <div className="icon-3d" style={{ fontSize: '50px', marginBottom: '12px' }}>⭐</div>
            <h4 style={{ marginBottom: '6px' }}>قائمة الاهتمام</h4>
            <p style={{ fontSize: '13px', opacity: 0.8 }}>أعضائي المفضلون</p>
          </div>

          <div 
            data-testid="blocked-card"
            className="card profile-card card-float stagger-item" 
            onClick={() => { playClick(); navigate('/blocked'); }}
            style={{ textAlign: 'center', cursor: 'pointer' }}
          >
            <div className="icon-3d" style={{ fontSize: '50px', marginBottom: '12px' }}>🚫</div>
            <h4 style={{ marginBottom: '6px' }}>قائمة التجاهل</h4>
            <p style={{ fontSize: '13px', opacity: 0.8 }}>الأعضاء المحظورون</p>
          </div>
        </div>

        {/* Subscription Status Card */}
        {!subscriptionStatus?.is_premium && (
          <div className="card subscription-cta-card" style={{ 
            marginBottom: '14px',
            textAlign: 'center',
            padding: '28px'
          }}>
            <div style={{ fontSize: '50px', marginBottom: '16px' }}>💎</div>
            <h3 className="subscription-cta-title">
              اشترك الآن واستمتع بكل المميزات!
            </h3>
            <p className="subscription-cta-text">
              احصل على طلبات ورسائل غير محدودة
            </p>
            <div className="subscription-cta-limits">
              <span>
                <strong>حدودك الحالية:</strong> {subscriptionStatus?.limits?.connection_requests || 3} طلب | {subscriptionStatus?.limits?.messages_per_chat || 3} رسائل لكل محادثة
              </span>
            </div>
            <br/>
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/pricing')}
              style={{
                background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                padding: '14px 40px',
                fontSize: '16px',
                fontWeight: '700',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)'
              }}
            >
              عرض الباقات 🚀
            </button>
          </div>
        )}

        {/* Features Section */}
        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <h3 style={{ 
            marginBottom: '14px', 
            fontSize: '22px',
            fontWeight: '700'
          }}>
            ✨ لماذا وِداد؟
          </h3>
          <div className="grid grid-3">
            <div className="card" style={{ textAlign: 'center', padding: '24px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>💕</div>
              <h4 style={{ marginBottom: '10px', fontSize: '17px' }}>توافق حقيقي</h4>
              <p style={{ fontSize: '14px', lineHeight: '1.7', opacity: 0.8 }}>نجمع بين المتشابهين في الاهتمامات والتخصص</p>
            </div>
            <div className="card" style={{ textAlign: 'center', padding: '24px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
              <h4 style={{ marginBottom: '10px', fontSize: '17px' }}>أمان وخصوصية</h4>
              <p style={{ fontSize: '14px', lineHeight: '1.7', opacity: 0.8 }}>حماية معلوماتك وخصوصيتك أولوية قصوى</p>
            </div>
            <div className="card" style={{ textAlign: 'center', padding: '24px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>💍</div>
              <h4 style={{ marginBottom: '10px', fontSize: '17px' }}>زواج شرعي</h4>
              <p style={{ fontSize: '14px', lineHeight: '1.7', opacity: 0.8 }}>منصة جادة تهدف للزواج الشرعي فقط</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Notification Permission Banner */}
      <NotificationBanner />

      {/* رسالة تحفيزية عشوائية */}
      <MotivationalToast show={showMotivation} onClose={() => setShowMotivation(false)} />

      {/* Footer Links */}
      <div style={{ 
        textAlign: 'center', 
        padding: '30px 20px 20px',
        borderTop: '1px solid var(--border-color)',
        marginTop: '20px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap', marginBottom: '12px' }}>
          <span onClick={() => navigate('/contact')} style={{ cursor: 'pointer', color: 'var(--text-light)', fontSize: '13px' }}>اتصل بنا</span>
          <span onClick={() => navigate('/refund-policy')} style={{ cursor: 'pointer', color: 'var(--text-light)', fontSize: '13px' }}>سياسة الاسترداد</span>
          <span onClick={() => navigate('/terms')} style={{ cursor: 'pointer', color: 'var(--text-light)', fontSize: '13px' }}>الشروط والأحكام</span>
          <span onClick={() => navigate('/privacy')} style={{ cursor: 'pointer', color: 'var(--text-light)', fontSize: '13px' }}>سياسة الخصوصية</span>
        </div>
        <p style={{ color: 'var(--text-light)', fontSize: '12px', opacity: 0.6, margin: 0 }}>
          2026 وِداد - جميع الحقوق محفوظة
        </p>
      </div>
    </div>
  );
}

export default Dashboard;
